import React from 'react'
import { useTranslation } from 'react-i18next'
import { 
  HelpCircle, 
  Book, 
  MessageCircle, 
  Video,
  FileText,
  Users,
  Download,
  Mail,
  Clock,
  Search
} from 'lucide-react'

const Help: React.FC = () => {
  const { t } = useTranslation()

  const helpSections = [
    {
      icon: Book,
      title: t('help.gettingStarted'),
      description: t('help.gettingStartedDescription'),
      link: '#getting-started'
    },
    {
      icon: HelpCircle,
      title: t('help.features'),
      description: t('help.featuresDescription'),
      link: '#features'
    },
    {
      icon: MessageCircle,
      title: t('help.troubleshooting'),
      description: t('help.troubleshootingDescription'),
      link: '#troubleshooting'
    },
    {
      icon: FileText,
      title: t('help.faq'),
      description: t('help.faqDescription'),
      link: '#faq'
    },
    {
      icon: Users,
      title: t('help.contact'),
      description: t('help.contactDescription'),
      link: '#contact'
    },
    {
      icon: Book,
      title: t('help.documentation'),
      description: t('help.documentationDescription'),
      link: '#documentation'
    }
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">{t('help.title')}</h1>
        <p className="text-gray-400">{t('help.subtitle')}</p>
      </div>

      {/* Search Bar */}
      <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder={t('help.searchPlaceholder')}
            className="w-full pl-10 pr-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      {/* Help Categories */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {helpSections.map((section, index) => {
          const Icon = section.icon
          return (
            <a
              key={index}
              href={section.link}
              className="bg-dark-800 rounded-lg p-6 border border-dark-700 hover:border-primary/50 transition-colors group"
            >
              <div className="flex items-start space-x-4">
                <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                  <Icon className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">{section.title}</h3>
                  <p className="text-sm text-gray-400">{section.description}</p>
                </div>
              </div>
            </a>
          )
        })}
      </div>

      {/* Quick Links */}
      <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
        <h2 className="text-xl font-semibold text-white mb-4">{t('help.quickLinks')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a href="#download" className="flex items-center space-x-3 text-gray-300 hover:text-primary transition-colors">
            <Download className="h-5 w-5" />
            <span>{t('help.downloadApp')}</span>
          </a>
          <a href="#videos" className="flex items-center space-x-3 text-gray-300 hover:text-primary transition-colors">
            <Video className="h-5 w-5" />
            <span>{t('help.videoTutorials')}</span>
          </a>
          <a href="#forum" className="flex items-center space-x-3 text-gray-300 hover:text-primary transition-colors">
            <Users className="h-5 w-5" />
            <span>{t('help.communityForum')}</span>
          </a>
        </div>
      </div>

      {/* Contact Support */}
      <div className="bg-blue-900/20 border border-blue-900/50 rounded-lg p-6">
        <h3 className="text-blue-400 font-medium mb-2">{t('help.needMoreHelp')}</h3>
        <p className="text-sm text-blue-300 mb-4">{t('help.contactSupport')}</p>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Mail className="h-4 w-4 text-blue-400" />
            <span className="text-sm text-blue-300">{t('help.emailSupport')}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Clock className="h-4 w-4 text-blue-400" />
            <span className="text-sm text-blue-300">{t('help.responseTime')}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Help